import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  ReadOnlySubject,
  Subject,
  PersonalData,
  Contacts,
  PaymentMethod,
  Address,
  useCreateSubjectPaymentMethodMutation,
  useLazyGetSubjectQuery,
  useUpdateSubjectPaymentMethodMutation,
  useUpdatePlainSubjectContactsMutation,
  useUpdateSubjectAddressMutation,
  useCreateSubjectAddressMutation,
  useDeleteSubjectAddressMutation,
  useDeleteSubjectPaymentMethodMutation,
  useUpdateSubjectPersonalDataMutation,
  useLazyGetSubjectHistoryQuery,
  SubjectHistory,
  useInsertSubjectMutation,
} from "../redux/apiSpecifications/apiSubjects";

export const useSubjects = () => {
  const [getSubject] = useLazyGetSubjectQuery();

  const [insertSubject] = useInsertSubjectMutation();

  const [updateSubjectPersonalData] = useUpdateSubjectPersonalDataMutation();

  const [updatePlainSubject] = useUpdatePlainSubjectContactsMutation();
  const [createSubjectAddress] = useCreateSubjectAddressMutation();
  const [updateSubjectAddress] = useUpdateSubjectAddressMutation();
  const [deleteSubjectAddress] = useDeleteSubjectAddressMutation();
  const [createSubjectPaymentMethod] = useCreateSubjectPaymentMethodMutation();
  const [updateSubjectPaymentMethod] = useUpdateSubjectPaymentMethodMutation();
  const [deleteSubjectPaymentMethod] = useDeleteSubjectPaymentMethodMutation();
  const [getSubjectHistory] = useLazyGetSubjectHistoryQuery();

  const getSubjectPromise = (
    id: number,
    referenceDate?: string
  ): Promise<ReadOnlySubject | undefined> =>
    new Promise(async (resolve, reject) => {
      const response = await getSubject({
        id: id,
        referenceDate: referenceDate,
      });

      const getResponse = response as {
        data: ReadOnlySubject;
      };

      if (getResponse.data) {
        resolve(getResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const insertSubjectPromise = (subject: Subject): Promise<Subject> =>
    new Promise(async (resolve, reject) => {
      const response = await insertSubject({ subject: subject });

      const postResponse = response as {
        data: Subject;
      };

      if (postResponse.data) {
        resolve(postResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const updateSubjectPersonalDataPromise = (
    subjectId: number,
    personalData: PersonalData
  ): Promise<PersonalData> =>
    new Promise(async (resolve, reject) => {
      const response = await updateSubjectPersonalData({
        subjectId,
        personalData,
      });

      const putResponse = response as {
        data: PersonalData;
      };

      if (putResponse.data) {
        resolve(putResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const updatePlainSubjectContactsPromise = (
    subjectId: number,
    contacts: Contacts
  ): Promise<Contacts> =>
    new Promise(async (resolve, reject) => {
      const response = await updatePlainSubject({
        subjectId,
        contacts,
      });

      const putResponse = response as {
        data: Contacts;
      };

      if (putResponse.data) {
        resolve(putResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const createSubjectAddressPromise = (
    subjectId: number,
    address: Address
  ): Promise<Address[]> =>
    new Promise(async (resolve, reject) => {
      const response = await createSubjectAddress({
        subjectId,
        address,
      });

      const postResponse = response as {
        data: Address[];
      };

      if (postResponse.data) {
        resolve(postResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const updateSubjectAddressPromise = (
    subjectId: number,
    address: Address
  ): Promise<Address[]> =>
    new Promise(async (resolve, reject) => {
      const response = await updateSubjectAddress({
        subjectId,
        addressId: address.id!,
        address,
      });

      const putResponse = response as {
        data: Address[];
      };

      if (putResponse.data) {
        resolve(putResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const deleteSubjectAddressPromise = (
    subjectId: number,
    addressId: number
  ): Promise<PaymentMethod[]> =>
    new Promise(async (resolve, reject) => {
      const response = await deleteSubjectAddress({
        subjectId,
        addressId,
      });

      const deleteResponse = response as {
        data: Address[];
      };

      if (deleteResponse.data) {
        resolve(deleteResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const createSubjectPaymentMethodPromise = (
    subjectId: number,
    paymentMethod: PaymentMethod
  ): Promise<PaymentMethod[]> =>
    new Promise(async (resolve, reject) => {
      const response = await createSubjectPaymentMethod({
        subjectId,
        paymentMethod,
      });

      const postResponse = response as {
        data: PaymentMethod[];
      };

      if (postResponse.data) {
        resolve(postResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const updateSubjectPaymentMethodPromise = (
    subjectId: number,
    paymentMethod: PaymentMethod
  ): Promise<PaymentMethod[]> =>
    new Promise(async (resolve, reject) => {
      const response = await updateSubjectPaymentMethod({
        subjectId,
        paymentMethodId: paymentMethod.id!,
        paymentMethod,
      });

      const postResponse = response as {
        data: PaymentMethod[];
      };

      if (postResponse.data) {
        resolve(postResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const deleteSubjectPaymentMethodPromise = (
    subjectId: number,
    paymentMethodId: number
  ): Promise<PaymentMethod[]> =>
    new Promise(async (resolve, reject) => {
      const response = await deleteSubjectPaymentMethod({
        subjectId,
        paymentMethodId,
      });

      const deleteResponse = response as {
        data: PaymentMethod[];
      };

      if (deleteResponse.data) {
        resolve(deleteResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const getSubjectHistoryPromise = (
    subjectId: number
  ): Promise<SubjectHistory[]> =>
    new Promise(async (resolve, reject) => {
      const response = await getSubjectHistory({
        subjectId,
      });

      const getResponse = response as {
        data: SubjectHistory[];
      };

      if (getResponse.data) {
        resolve(getResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  return {
    getSubjectPromise,
    insertSubjectPromise,
    updateSubjectPersonalDataPromise,
    updatePlainSubjectContactsPromise,
    createSubjectAddressPromise,
    updateSubjectAddressPromise,
    deleteSubjectAddressPromise,
    createSubjectPaymentMethodPromise,
    updateSubjectPaymentMethodPromise,
    deleteSubjectPaymentMethodPromise,
    getSubjectHistoryPromise,
  };
};
